import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Mail, Send, Loader2, Check, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import SEOHead from '../components/SEOHead';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import axios from 'axios';

import API_URL from '../config/api';

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/contact`, form);
      setSubmitted(true);
      toast.success(t('contact.success_toast', 'Message sent! We\'ll get back to you soon.'));
    } catch (err) {
      const msg = err.response?.data?.detail || t('contact.error', 'Failed to send message. Please try again.');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <SEOHead title="Contact Us" description="Get in touch with the LusoLove team. We're here to help with any questions about the Portuguese dating platform." path="/contact" />
      {/* Header */}
      <header className="fixed top-0 w-full z-50 backdrop-blur-xl bg-[#FAF9F6]/90 border-b border-[#E5E3DB]/50">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="contact-logo">
            <Heart className="w-9 h-9 text-[#6E1A24] group-hover:scale-110 transition-transform" strokeWidth={1.5} fill="currentColor" fillOpacity={0.1} />
            <span className="text-2xl font-serif font-medium text-[#2D2D2A]">LusoLove</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link to="/login">
              <Button className="bg-[#6E1A24] text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-[#8A2331] transition-all" data-testid="contact-signin">
                {t('landing.sign_in')}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="pt-32 pb-24 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Title */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#6E1A24]/10 mb-6">
              <MessageSquare className="w-8 h-8 text-[#6E1A24]" strokeWidth={1.5} />
            </div>
            <h1 className="text-4xl sm:text-5xl font-serif font-light text-[#2D2D2A] mb-4" data-testid="contact-title">
              {t('contact.title')}
            </h1>
            <p className="text-lg text-[#6C6C67] font-light max-w-lg mx-auto">
              {t('contact.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Form */}
            <div className="lg:col-span-3">
              {submitted ? (
                <div className="bg-white rounded-3xl border border-[#E5E3DB] p-12 text-center" data-testid="contact-success">
                  <div className="w-16 h-16 rounded-full bg-[#2D6A4F]/10 flex items-center justify-center mx-auto mb-6">
                    <Check className="w-8 h-8 text-[#2D6A4F]" />
                  </div>
                  <h3 className="text-2xl font-serif font-medium text-[#2D2D2A] mb-3">{t('contact.success_title')}</h3>
                  <p className="text-[#6C6C67] mb-8">{t('contact.success_desc')}</p>
                  <Button
                    onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: '', message: '' }); }}
                    className="bg-[#6E1A24] text-white rounded-full px-8 py-3 font-medium hover:bg-[#8A2331] transition-all"
                    data-testid="contact-send-another"
                  >
                    {t('contact.send_another')}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white rounded-3xl border border-[#E5E3DB] p-8 sm:p-10 space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-medium text-[#2D2D2A] block mb-2">{t('contact.name_label')}</label>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={(e) => update('name', e.target.value)}
                        className="w-full px-4 py-3.5 bg-[#FAF9F6] border border-[#E5E3DB] rounded-xl focus:outline-none focus:border-[#6E1A24] focus:ring-4 focus:ring-[#6E1A24]/5 transition-all"
                        placeholder={t('contact.name_placeholder')}
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#2D2D2A] block mb-2">{t('contact.email_label')}</label>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => update('email', e.target.value)}
                        className="w-full px-4 py-3.5 bg-[#FAF9F6] border border-[#E5E3DB] rounded-xl focus:outline-none focus:border-[#6E1A24] focus:ring-4 focus:ring-[#6E1A24]/5 transition-all"
                        placeholder="your@email.com"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-[#2D2D2A] block mb-2">{t('contact.subject_label')}</label>
                    <input
                      type="text"
                      required
                      value={form.subject}
                      onChange={(e) => update('subject', e.target.value)}
                      className="w-full px-4 py-3.5 bg-[#FAF9F6] border border-[#E5E3DB] rounded-xl focus:outline-none focus:border-[#6E1A24] focus:ring-4 focus:ring-[#6E1A24]/5 transition-all"
                      placeholder={t('contact.subject_placeholder')}
                      data-testid="contact-subject"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-[#2D2D2A] block mb-2">{t('contact.message_label')}</label>
                    <textarea
                      required
                      rows={5}
                      value={form.message}
                      onChange={(e) => update('message', e.target.value)}
                      className="w-full px-4 py-3.5 bg-[#FAF9F6] border border-[#E5E3DB] rounded-xl focus:outline-none focus:border-[#6E1A24] focus:ring-4 focus:ring-[#6E1A24]/5 transition-all resize-none"
                      placeholder={t('contact.message_placeholder')}
                      data-testid="contact-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-[#6E1A24] text-white rounded-xl w-full py-4 font-semibold text-base hover:bg-[#8A2331] hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="contact-submit"
                  >
                    {loading ? (
                      <><Loader2 className="w-5 h-5 animate-spin" />{t('contact.sending')}</>
                    ) : (
                      <><Send className="w-5 h-5" />{t('contact.send_btn')}</>
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-3xl border border-[#E5E3DB] p-8">
                <Mail className="w-6 h-6 text-[#6E1A24] mb-4" />
                <h3 className="font-serif font-medium text-[#2D2D2A] mb-2">{t('contact.email_us')}</h3>
                <a href="mailto:contact@lusolove.com" className="text-[#6E1A24] font-medium hover:underline" data-testid="contact-email-link">
                  contact@lusolove.com
                </a>
                <p className="text-sm text-[#6C6C67] mt-2">{t('contact.response_time')}</p>
              </div>

              <div className="bg-[#F5F2EB] rounded-3xl p-8">
                <h3 className="font-serif font-medium text-[#2D2D2A] mb-3">{t('contact.faq_hint_title')}</h3>
                <p className="text-sm text-[#6C6C67] mb-4">{t('contact.faq_hint_desc')}</p>
                <Link to="/faq" className="text-[#6E1A24] font-medium text-sm hover:underline" data-testid="contact-faq-link">
                  {t('contact.faq_link')} →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 bg-[#2D2D2A] text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <Heart className="w-6 h-6 text-[#C97A63]" strokeWidth={1.5} fill="currentColor" fillOpacity={0.3} />
            <span className="text-xl font-serif">LusoLove</span>
          </div>
          <div className="flex gap-8 text-sm text-white/60">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link>
            <Link to="/faq" className="hover:text-white transition-colors">{t('nav.faq', 'FAQ')}</Link>
            <Link to="/contact" className="hover:text-white transition-colors">{t('nav.contact', 'Contact')}</Link>
          </div>
          <p className="text-sm text-white/40">{t('landing.footer_tagline')}</p>
        </div>
      </footer>
    </div>
  );
}
